/**
 * PGF RIGHT PANEL - Draft Room AI panel.
 *
 * [Phase 2c.6-B v1.1 - Analysis panel collapse]
 * Four-persona HC / Scout / Doctor / CFO tab UI removed. Replaced by a
 * single Curly consensus card (`InDraftAnalysisCard`) lazy-fetched from
 * `/api/draft/{id}/player-analysis`.
 *
 * Tier semantics (Gap-1 verdict B): Curly consensus is FREE FOR ALL.
 * "Get Advice" recommendation block (`/api/draft-room/recommend`)
 * remains paid - that's a separate feature out of 2c.6-B scope. The
 * outer wrapper used to lock the entire panel behind the paid gate;
 * now it locks only the recommendation block. Free users see the
 * consensus card + an upgrade CTA where the Get Advice button used to be.
 *
 * Four-persona prompts retired but preserved as dormant code at
 * `engines/analytics_personas.py` (TD-43, Phase 4 candidate). Endpoint
 * `/api/draft-room/analytics` still serves them.
 *
 * Stripe paid-tier billing patterns are being finalized with co-founder
 * - DO NOT pre-implement gating logic here for 2d. See PRD TD-49.
 */
import { useState } from "react";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";
import { useDraft } from "../../contexts/DraftContext";
import PositionBadge from "../PositionBadge";
import { Brain, Lock } from "lucide-react";
// [TD-47.6a-ui Gap F.1] InDraftAnalysisCard deleted; Player Analysis Canvas
// (TD-47.6a-ui Zone 2) is now the canonical analysis surface for auction.
// RightPanel is no longer mounted in DraftRoom.jsx but stays in the repo
// as dormant code for the paid-tier "Get Advice" recommendation surface
// (banked under TD-49 + future Phase 2d Stripe rollout).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DraftRoomPanel({ leagueId, user, isPaid }) {
  const { nomination } = useDraft();
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);

  const fetchRec = async () => {
    if (!nomination) return;
    setRecLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/draft-room/recommend`,
        {
          league_id: leagueId,
          player_id: nomination.player_id,
          current_bid: nomination.current_bid,
          manager_id: user.id,
        },
        { headers: authHeaders() },
      );
      setRec(data.recommendation);
    } catch {
      /* swallow - Get Advice surface stays optional */
    } finally {
      setRecLoading(false);
    }
  };

  const actionColor = {
    BID_UP: "text-[#00C853]",
    HOLD: "text-yellow-400",
    PASS: "text-[#FF4444]",
  };

  // Player object for InDraftAnalysisCard - derived from current
  // nomination so the consensus card refetches on every new nomination.
  const analysisPlayer = nomination?.player_id
    ? {
        id: nomination.player_id,
        name: nomination.player?.name,
        position: nomination.player?.position,
        nfl_team: nomination.player?.nfl_team,
      }
    : null;

  return (
    <div className="bg-[#1C1C1C] border-2 border-[#FFD700]/40 rounded-2xl flex flex-col h-full overflow-hidden animate-gold-glow">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#FFD700]/20 shrink-0">
        <Brain size={15} className="text-[#FFD700]" />
        <span className="font-display font-black text-sm uppercase tracking-wider text-[#FFD700]">
          Draft Room
        </span>
        {isPaid && (
          <span
            className="ml-auto bg-[#FFD700] text-[#111111] font-display font-black text-[10px] px-2 py-0.5 rounded uppercase tracking-wider"
            data-testid="right-panel-pro-badge"
          >
            Pro
          </span>
        )}
      </div>

      {nomination ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Current player chip */}
          <div className="flex items-center gap-2">
            <PositionBadge position={nomination.player?.position} />
            <span className="font-display font-bold text-sm uppercase text-[#F5F5F5] truncate">
              {nomination.player?.name}
            </span>
          </div>

          {/* [TD-47.6a-ui Gap F.1] Curly consensus card removed; surface
              relocated to PlayerAnalysisCanvas in DraftRoom Zone 2. */}

          {/* Get Advice (recommendation) - stays paid-tier in 2c.6-B. */}
          {isPaid ? (
            <>
              <button
                onClick={fetchRec}
                disabled={recLoading}
                data-testid="get-recommendation-btn"
                className="w-full bg-[#FFD700]/10 hover:bg-[#FFD700]/20 border border-[#FFD700]/30 text-[#FFD700] font-display font-bold text-xs uppercase py-2 rounded-lg transition-all disabled:opacity-50"
              >
                {recLoading ? "Analyzing..." : rec ? "Refresh Advice" : "Get Advice"}
              </button>

              {rec && (
                <div
                  className="bg-[#252525] rounded-lg p-3 space-y-2"
                  data-testid="recommendation-result"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-display font-black text-lg uppercase ${actionColor[rec.action] || "text-[#F5F5F5]"}`}
                    >
                      {rec.action?.replace(/_/g, " ")}
                    </span>
                    <span className="font-mono font-bold text-[#FFD700] text-sm">
                      Max: ${rec.max_bid}
                    </span>
                  </div>
                  <p className="font-body text-xs text-[rgba(255,255,255,0.50)] leading-relaxed">
                    {rec.reasoning}
                  </p>
                  {rec.fallback && (
                    <p className="font-body text-xs text-[rgba(255,255,255,0.40)]">
                      Alt: {rec.fallback}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              className="bg-[#1A1A1A] border border-[#FFD700]/25 rounded-lg p-3 flex items-center gap-3"
              data-testid="get-advice-paywall"
            >
              <Lock size={14} className="text-[#FFD700] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-body text-[11px] text-[rgba(255,255,255,0.65)] leading-snug">
                  AI bid recommendations are a Pro feature.
                </p>
              </div>
              <button
                className="bg-[#FFD700] hover:bg-[#FFE033] text-[#111111] font-display font-black text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-all shrink-0"
                data-testid="upgrade-cta-inline"
              >
                Upgrade
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center p-4 text-center">
          <p className="text-[rgba(255,255,255,0.35)] font-body text-sm">
            Waiting for next nomination...
          </p>
        </div>
      )}
    </div>
  );
}

export default function RightPanel({ draftState, leagueId, user }) {
  const isPaid =
    user?.subscription_tier === "paid" || user?.subscription_tier === "pro";
  return (
    <div className="flex flex-col h-full">
      <DraftRoomPanel
        draftState={draftState}
        leagueId={leagueId}
        user={user}
        isPaid={isPaid}
      />
    </div>
  );
}
